<template>
  <section v-editable="blok" :data-uid="blok._uid" class="cb-change">
    <div v-if="!isFormSent" class="form">
      <div v-for="input in formData" :key="input.name">
        <span v-if="input.name === 'salutation'" class="additional-label">Anrede *</span>
        <div v-if="input.name === 'familyNumber'" class="additional-label additional-label--margin">
          Familiennummer/Kundennummer
          <strong>oder</strong>
          Versichertennummer *
        </div>
        <div v-if="input.name === 'familyNumber'" class="two-columned">
          <PflegeInput
            :id="input.name"
            v-model="input.value"
            :error-message="input.errorMessage"
            :has-error="input.hasError"
            :is-textarea="input.isTextarea"
            :label="setLabel(input)"
            :maxlength="input.maxLength"
            :name="input.name"
            :placeholder="input.placeholder"
            :required="true"
            :type="input.type"
            @blur="!blok.isCallCenterForm ? () => {} : validateInput(input)"
          />

          <PflegeInput
            :id="inputFields.insuranceNumber.name"
            v-model="inputFields.insuranceNumber.value"
            :error-message="inputFields.insuranceNumber.errorMessage"
            :has-error="inputFields.insuranceNumber.hasError"
            :is-textarea="inputFields.insuranceNumber.isTextarea"
            :label="setLabel(inputFields.insuranceNumber)"
            :maxlength="inputFields.insuranceNumber.maxLength"
            :name="inputFields.insuranceNumber.name"
            :placeholder="inputFields.insuranceNumber.placeholder"
            :required="true"
            :type="inputFields.insuranceNumber.type"
            @blur="
              !blok.isCallCenterForm ? () => {} : validateInput(inputFields.insuranceNumber)
            "
          />
        </div>

        <div v-else-if="input.name !== 'insuranceNumber'">
          <PflegeDropdown
            v-if="input.type === 'select'"
            :id="input.name"
            v-model="input.value"
            :error-message="input.errorMessage"
            :has-error="input.hasError"
            :label="setLabel(input)"
            :name="input.name"
            :options="input.options"
            :placeholder="input.placeholder"
            :required="true"
            data-pw="list"
            @update:model-value="validateInput(input)"
          />
          <PflegeCheckbox
            v-else-if="input.type === 'checkbox'"
            :id="input.name"
            v-model="input.checked"
            :error-message="input.errorMessage"
            :has-error="input.hasError"
            :name="input.name"
            :required="true"
            data-pw="textarea"
            @blur="validateInput(input)"
          >
            <template #label>
              <!-- eslint-disable vue/no-v-html -->
              <p v-html="setLabel(input)"></p>
            </template>
          </PflegeCheckbox>
          <PflegeRadio
            v-else-if="input.type === 'radio'"
            :id="input.name"
            v-model="input.value"
            :error-message="input.errorMessage"
            :has-error="input.hasError"
            :name="input.name"
            :options="input.options"
            :required="true"
            @blur="validateInput(input)"
          />
          <PflegeInput
            v-else
            :id="input.name"
            v-model="input.value"
            :error-message="input.errorMessage"
            :has-error="input.hasError"
            :is-textarea="input.isTextarea"
            :label="setLabel(input)"
            :maxlength="input.maxLength"
            :name="input.name"
            :placeholder="input.placeholder"
            :required="true"
            :type="input.type"
            @blur="validateInput(input, blok.isCallCenterForm)"
          />
        </div>
      </div>

      <p class="additional-label">
        * Pflichtangaben
      </p>

      <PflegeButton
        :label="blok.submit ?? 'Abschicken'"
        data-pw="abschicken"
        @click="sendForm"
      />
    </div>

    <div v-else data-pw="success-message">
      <SepacRichtextResolver :blok="blok.submitsuccessmessage" />
    </div>
  </section>
</template>

<script setup lang="ts">
import type { RichtextContent } from '@webcarelbj/shared-components'
import { useCbModule, type InputField } from './cbModule'
import { useRoute } from 'vue-router'

export interface CbChangeProps {
  blok: {
    _uid: string
    isCallCenterForm: boolean
    submit?: string
    submitsuccessmessage: RichtextContent
  }
}

interface CbChangeFetchData {
  success: boolean
  [key: string]: unknown
} 

const { blok } = defineProps<CbChangeProps>()

const { disableExternalTracking } = useTracking()
disableExternalTracking()

const isFormSent = ref(false)

const { inputFields, validateInput, validateAllInputs, getAllData, hasAnyErrors } = useCbModule()
const route = useRoute()
Object.keys(route.query).forEach((key) => {
  const field = inputFields[key]
  if (!field) return

  const queryValue = Array.isArray(route.query[key]) ? route.query[key][0] : route.query[key]
  if (queryValue != null) {
    switch (field.type) {
      case 'date': {
        // Validate date format and logical correctness
        const date = new Date(queryValue)
        if (!isNaN(date.getTime()) && /^\d{4}-\d{2}-\d{2}$/.exec(queryValue)) {
          field.value = queryValue
          validateInput(field, blok.isCallCenterForm)
        }
        break
      }
      case 'radio':
      case 'select': {
        if (field.options?.some((option) => option.value === queryValue)) {
          field.value = queryValue
          validateInput(field, blok.isCallCenterForm)
        }
        break
      }
      case 'checkbox': {
        // Check should be ignored
        break
      }
      default: {
        // For most types, we can directly assign the value
        field.value = field.maxLength ? queryValue.slice(0, field.maxLength) : queryValue
        validateInput(field, blok.isCallCenterForm)
        break
      }
    }
  }
})

const formData = computed(() => {
  if (blok.isCallCenterForm) {
    Object.assign(inputFields.consent, {
      label: 'Datenverarbeitung akzeptiert',
    })
  }

  return inputFields
})

const setLabel = (input: InputField) => {
  const isRequired =
    input.name === 'familyNumber' ||
    input.name === 'insuranceNumber' ||
    (blok.isCallCenterForm && input.name === 'email')
      ? ''
      : ' *'

  return input.label + isRequired
}

const sendForm = async () => {
  validateAllInputs(blok.isCallCenterForm)
  if (!hasAnyErrors(formData.value)) {
    if (formData.value.email?.value === '' && !!blok.isCallCenterForm) {
      delete formData.value.email
    }

    const url = '/api/curabox-change-form/' + (blok.isCallCenterForm ? 'callcenter' : 'customers')

    try {
      const data = await $fetch<CbChangeFetchData>(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(getAllData(formData.value, !!blok.isCallCenterForm)),
      })
      if (!data.success) {
        throw new Error('Change form api did not return success')
      }
      isFormSent.value = true
    } catch (error) {
      alert('Fehler beim Senden der Daten. Bitte versuchen Sie es später.')
      console.error('Error sending change form', error)
    }
  }
}
</script>

<style lang="scss" scoped>
section {
  display: flex;
  justify-content: center;
  padding: 2rem 0;
}

.form {
  width: 100%;

  /* stylelint-disable-next-line custom-property-pattern -- comes from the storybook package */
  max-width: var(--maxContainerChildWidth);
}

.two-columned {
  @media (width >= 810px) {
    display: flex;
    gap: 2rem;
  }
}

.checkbox {
  padding: 1rem 0;
}

.additional-label {
  font-size: smaller;

  &--margin {
    margin-top: 1rem;
  }
}
</style>
